import { AppThunkAction } from "..";
import { KnownAction } from "./actionTypes";
import { OJEUMWD3Hub } from "./signalRHub";
import {
    OJEUMWD3Settings,
    Errors,
    DisplaySettings,
    RegulationSettings,
    HeatingSettings,
    FloorSensorSettings,
    AwayModeSettings,
    DateTimeSettings,
    WifiSettings,
    WarrantyInfo,
    DeviceInfo,
    CreateOJEUMWD3Request,
    LocaleSettings,
    ClockSettings,
    PSUFunctionMode,
    ProtectiveFunctions
} from "./models";
import { toBase64 } from "../../Utility";

export enum OJEUMWD3Action {
    CreateNew = "CreateNew",
    Remove = "RemoveThermostat",
    Connect = "Connect",
    Link = "Link",
    Unlink = "Unlink",
    Disconnect = "Disconnect",
    IncreaseSetpoint = "IncreaseSetpoint",
    DecreaseSetpoint = "DecreaseSetpoint",
    Save = "SaveThermostat",
    ToggleTimer = "ToggleTimer",
    ToggleHeatRelay = "ToggleHeatRelay",
    ToggleOpenWindowDetected = "ToggleOpenWindowDetected",
    ToggleFrostProtection = "ToggleFrostProtection",
    ToggleReconnect = "ToggleReconnect",
    FactoryReset = "RequestFactoryReset"
}

export const actionCreator = {
    createNew: (workspace: number, request: CreateOJEUMWD3Request | null = null, amount: number = 1): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("CreateNewThermostat", workspace, amount, request).catch(alert);
    },
    addExisting: (workspace: number, email: string, pageNumber: number): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("AddExisting", workspace, email, pageNumber).catch(alert);
    },
    invoke: (id: string, action: OJEUMWD3Action): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send(action, id).catch(alert);
    },
    updateSettings: (id: string, settings: OJEUMWD3Settings): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("UpdateSettings", id, settings).catch(alert);
    },
    updateProtectiveFunctions: (id: string, protectiveFunctions: ProtectiveFunctions): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("UpdateProtectiveFunctions", id, protectiveFunctions).catch(alert);
    },
    setSetpoint: (id: string, setpoint: number): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("SetSetpoint", id, setpoint).catch(alert);
    },
    setAutoMode: (id: string): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("SetAutoMode", id).catch(alert);
    },
    setManualMode: (id: string, setpoint: number): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("SetManualMode", id, setpoint).catch(alert);
    },
    setHoldMode: (id: string, setpoint: number, endTime: Date | null, isPermanent: boolean | null): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("SetHoldMode", id, setpoint, endTime, isPermanent).catch(alert);
    },
    cancelHoldMode: (id: string): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("CancelHoldMode", id).catch(alert);
    },
    setServerUrl: (serverUrl: string): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("SetServerUrl", serverUrl).catch(alert);
    },
    setErrors: (id: string, errors: Errors): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("SetErrors", id, errors).catch(alert);
    },
    setStandbyState: (id: string, isInStandby: boolean): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("SetStandbyState", id, isInStandby).catch(alert);
    },
    setCurrentScheduleEvent: (id: string, day: number, eventId: number): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("SetCurrentScheduleEvent", id, day, eventId).catch(alert);
    },
    setCurrentTemp: (id: string, newTemp: number): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("SetCurrentTemp", id, newTemp).catch(alert);
    },
    setPSUFunctionMode: (id: string, psuFunctionMode: PSUFunctionMode): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("SetPSUFunctionMode", id, psuFunctionMode).catch(alert);
    },
    fetchLoadableThermostats: (): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("FetchLoadableThermostats").catch(alert);
    },
    loadThermostats: (ids: string[]): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("LoadThermostats", ids).catch(alert);
    },
    deleteThermostats: (ids: string[]): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("DeleteThermostats", ids).catch(alert);
    },
    updateDeviceInfo: (id: string, deviceInfo: DeviceInfo): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        const obj = {
            ...deviceInfo,
            mcuUuid: toBase64(deviceInfo.mcuUuid),
            protocolVersion: toBase64(deviceInfo.protocolVersion)
        };
        OJEUMWD3Hub.send("UpdateDeviceInfo", id, obj).catch(alert);
    },
    updateDisplaySettings: (id: string, settings: DisplaySettings): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("UpdateDisplaySettings", id, settings).catch(alert);
    },
    updateWifiSettings: (id: string, settings: WifiSettings): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("UpdateWifiSettings", id, settings).catch(alert);
    },
    updateRegulationSettings: (id: string, settings: RegulationSettings): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("UpdateRegulationSettings", id, settings).catch(alert);
    },
    updateHeatingSettings: (id: string, settings: HeatingSettings): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("UpdateHeatingSettings", id, settings).catch(alert);
    },
    updateFloorSensorSettings: (id: string, settings: FloorSensorSettings): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("UpdateFloorSensorSettings", id, settings).catch(alert);
    },
    updateAwayModeSettings: (id: string, settings: AwayModeSettings): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("UpdateAwayModeSettings", id, settings).catch(alert);
    },
    updateLocaleSettings: (id: string, settings: LocaleSettings): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("UpdateLocaleSettings", id, settings).catch(alert);
    },
    updateClockSettings: (id: string, settings: ClockSettings): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("UpdateClockSettings", id, settings).catch(alert);
    },
    updateDateTimeSettings: (id: string, settings: DateTimeSettings): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("UpdateDateTimeSettings", id, settings).catch(alert);
    },
    updateWarrantyInfo: (id: string, info: WarrantyInfo): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("UpdateWarrantyInfo", id, info).catch(alert);
    },
    searchDistributors: (query: string): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("SearchDistributors", query).catch(alert);
    },
    searchUsers: (query: string): AppThunkAction<KnownAction> => (_dispatch, _getState) => {
        OJEUMWD3Hub.send("SearchUsers", query).catch(alert);
    }
};